import { useState } from "react";

import { currency } from "@/utils/formats";
import { formatLoyaltyPoints } from "@/lib/loyalty-points";

type Props = {
  availablePoints: number;
  appliedPoints: number;
  onApply: (points: number) => void;
  onRemove: () => void;
  disabled?: boolean;
};

export default function LoyaltyPointsApply({
  availablePoints,
  appliedPoints,
  onApply,
  onRemove,
  disabled = false,
}: Props) {
  const [counterInput, setCounterInput] = useState("");
  const isApplied = appliedPoints > 0;

  const counter = (() => {
    if (!counterInput) {
      return 0;
    }

    const parsed = Number.parseInt(counterInput, 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return 0;
    }

    return Math.min(parsed, availablePoints);
  })();

  const handleApply = () => {
    if (counter > 0) {
      onApply(counter);
    }
  };

  const handleRemove = () => {
    setCounterInput("");
    onRemove();
  };

  const handleCounterChange = (rawValue: string) => {
    if (rawValue === "") {
      setCounterInput("");
      return;
    }

    if (!/^\d+$/.test(rawValue)) {
      return;
    }

    const nextValue = Number.parseInt(rawValue, 10);
    if (nextValue <= availablePoints) {
      setCounterInput(rawValue);
    }
  };

  if (availablePoints <= 0) {
    return null;
  }

  return (
    <div className="rounded-lg border border-brand-100 bg-brand-50/60 p-4">
      <h3 className="text-sm font-semibold text-gray-900">Use loyalty points</h3>
      <p className="mt-1 text-sm text-gray-600">
        100 points = £1 off. You have {formatLoyaltyPoints(availablePoints)} points
        available.
      </p>

      {isApplied ? (
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between gap-3 rounded-md bg-white px-3 py-2 text-sm text-gray-800">
            <p>
              <span className="font-semibold">
                {formatLoyaltyPoints(appliedPoints)} points
              </span>{" "}
              applied ({currency(appliedPoints)} off)
            </p>
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled}
              className="font-medium text-brand-700 hover:text-brand-600"
            >
              Remove
            </button>
          </div>
          <p className="text-xs text-gray-500">
            {formatLoyaltyPoints(availablePoints - appliedPoints)} points remaining
          </p>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <div>
            <label htmlFor="online-points-input" className="sr-only">
              Points to redeem
            </label>
            <input
              id="online-points-input"
              inputMode="numeric"
              pattern="[0-9]*"
              value={counterInput}
              onChange={(event) => handleCounterChange(event.target.value)}
              placeholder="Enter points to use"
              disabled={disabled}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <button
            type="button"
            onClick={handleApply}
            disabled={disabled || counter <= 0}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Apply points
          </button>
          {counter > 0 ? (
            <p className="text-xs text-gray-600">
              This will take {formatLoyaltyPoints(counter)} points off your order (
              {currency(counter)}).
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
